import type { ChallengeCampaign, TaskStatus } from "@/types/challenges.types";

export type TierName = "TIER1" | "TIER2" | "TIER3" | "AMBASSADOR";

export interface TierConfig {
  name: TierName;
  displayName: string;
  minPoints: number;
  maxPoints: number;
  unlockMessage: string;
  taskMaxPoints: number;
  color: string;
  bgColor: string;
  textColor: string;
  welcomeMessage?: string;
}

export const TIER_CONFIG: Record<TierName, TierConfig> = {
  TIER1: {
    name: "TIER1",
    displayName: "Tier 1",
    minPoints: 0,
    maxPoints: 129,
    unlockMessage: "Start your journey",
    taskMaxPoints: 100, // Tasks with ≤100 points
    color: "gray",
    bgColor: "bg-gray-100",
    textColor: "text-gray-700",
  },
  TIER2: {
    name: "TIER2",
    displayName: "Tier 2",
    minPoints: 130,
    maxPoints: 799,
    unlockMessage: "Unlock at 250 points",
    taskMaxPoints: 200, // Tasks with >100 and ≤200 points
    color: "blue",
    bgColor: "bg-blue-100",
    textColor: "text-blue-700",
  },
  TIER3: {
    name: "TIER3",
    displayName: "Tier 3",
    minPoints: 800,
    maxPoints: 999,
    unlockMessage: "Unlock at 1050 points",
    taskMaxPoints: 250, // Tasks with >200 and ≤250 points
    color: "purple",
    bgColor: "bg-purple-100",
    textColor: "text-swapper-primary",
  },
  AMBASSADOR: {
    name: "AMBASSADOR",
    displayName: "Ambassador",
    minPoints: 1000,
    maxPoints: Infinity,
    unlockMessage: "Unlock at 1500 points",
    taskMaxPoints: Infinity, // Tasks with >250 points (final task)
    color: "purple",
    bgColor: "bg-gradient-to-r from-swapper-primary to-purple-600",
    textColor: "text-white",
    welcomeMessage: `Welcome to the Ambassador Tier!

You've made it this far which means you've completed the entire main questline.
Only a few tasks separate you from unlocking the official Swapper Ambassador Role.
Complete the final missions, claim your role, and step into the next chapter.
A lot of exciting things are waiting for you on the other side.
See you!`,
  },
};

export const TIER_ORDER: TierName[] = ["TIER1", "TIER2", "TIER3", "AMBASSADOR"];

/**
 * Tasks that should be placed in a specific tier regardless of their point value.
 * Key: task GUID, Value: forced tier name.
 */
const TASK_TIER_OVERRIDES: Partial<Record<string, TierName>> = {
  "29ad79af-e7b9-4477-a68f-2f0ca49d3f86": "TIER1", // Buy & Stake $SDL with Swapper
  "c075fb72-83a3-4cea-8b3b-28b2f6d5fb95": "TIER1", // Make a Direct Deposit on HyperSwap
  "d8262d8a-fd74-4e9d-b353-ac7ad4d40fdc": "TIER1", // Make a deposit to Overtime with Swapper
  "61a00876-57b2-43ed-a2a1-56cbb0398dbf": "TIER1", // Make a deposit to Prism with Swapper
  "6716a7b4-5a31-49b8-9d75-0cc3e9826a82": "TIER1", // Make a Deposit to Fluent with Swapper
};

/**
 * Get the user's current tier based on completed tasks (completion-based)
 */
export function getUserTier(
  completedTaskGuids: string[],
  allTasks: TaskStatus[],
  campaign: ChallengeCampaign
): TierName;

/**
 * Get the user's current tier based on total points (backward compatibility)
 * @deprecated Use completion-based overload instead
 */
export function getUserTier(totalPoints: number): TierName;

export function getUserTier(
  completedTaskGuidsOrPoints: string[] | number,
  allTasks?: TaskStatus[],
  campaign?: ChallengeCampaign
): TierName {
  // Backward compatibility: point-based tier calculation
  if (typeof completedTaskGuidsOrPoints === "number") {
    const totalPoints = completedTaskGuidsOrPoints;
    if (totalPoints >= TIER_CONFIG.AMBASSADOR.minPoints) return "AMBASSADOR";
    if (totalPoints >= TIER_CONFIG.TIER3.minPoints) return "TIER3";
    if (totalPoints >= TIER_CONFIG.TIER2.minPoints) return "TIER2";
    return "TIER1";
  }

  // New completion-based tier calculation
  const completedTaskGuids = completedTaskGuidsOrPoints;
  if (!allTasks || !campaign) return "TIER1";

  // Check tier completion from highest to lowest
  for (let i = TIER_ORDER.length - 1; i >= 0; i--) {
    const tierName = TIER_ORDER[i];
    const previousTiers = getPreviousTiers(tierName);

    // Check if all previous tiers are completed
    const allPreviousCompleted = previousTiers.every((prevTier) => {
      const tierTasks = getTasksInTier(prevTier, allTasks, campaign);
      return isTierCompleted(prevTier, completedTaskGuids, tierTasks);
    });

    if (allPreviousCompleted) {
      return tierName;
    }
  }

  return "TIER1"; // Default to TIER1
}

/**
 * Get the tier category a task belongs to based on its maxPoints.
 * If a taskGuid is provided and has an override in TASK_TIER_OVERRIDES, that tier is used instead.
 */
export const getTaskTier = (maxPoints: number, taskGuid?: string): TierName => {
  if (taskGuid && TASK_TIER_OVERRIDES[taskGuid]) {
    return TASK_TIER_OVERRIDES[taskGuid]!;
  }
  if (maxPoints <= TIER_CONFIG.TIER1.taskMaxPoints) return "TIER1";
  if (maxPoints <= TIER_CONFIG.TIER2.taskMaxPoints) return "TIER2";
  if (maxPoints <= TIER_CONFIG.TIER3.taskMaxPoints) return "TIER3";
  return "AMBASSADOR"; // Tasks with >250 points go to Ambassador tier
};

/**
 * Check if a task is unlocked for the user based on their current tier
 */
export const isTaskUnlocked = (taskTier: TierName, userTier: TierName): boolean => {
  const taskTierIndex = TIER_ORDER.indexOf(taskTier);
  const userTierIndex = TIER_ORDER.indexOf(userTier);
  return userTierIndex >= taskTierIndex;
};

/**
 * Check if a tier is unlocked for the user (completion-based)
 */
export function isTierUnlocked(
  tierName: TierName,
  completedTaskGuids: string[],
  allTasks: TaskStatus[],
  campaign: ChallengeCampaign
): boolean;

/**
 * Check if a tier is unlocked for the user (backward compatibility)
 * @deprecated Use completion-based overload instead
 */
export function isTierUnlocked(tierName: TierName, userPoints: number): boolean;

export function isTierUnlocked(
  tierName: TierName,
  completedTaskGuidsOrPoints: string[] | number,
  allTasks?: TaskStatus[],
  campaign?: ChallengeCampaign
): boolean {
  // Backward compatibility: point-based unlock check
  if (typeof completedTaskGuidsOrPoints === "number") {
    const userPoints = completedTaskGuidsOrPoints;
    return userPoints >= TIER_CONFIG[tierName].minPoints;
  }

  // New completion-based unlock check
  const completedTaskGuids = completedTaskGuidsOrPoints;
  if (!allTasks || !campaign) return tierName === "TIER1";

  // TIER1 is always unlocked
  if (tierName === "TIER1") return true;

  // Check if all previous tiers are completed
  const previousTiers = getPreviousTiers(tierName);

  return previousTiers.every((prevTier) => {
    const tierTasks = getTasksInTier(prevTier, allTasks, campaign);
    return isTierCompleted(prevTier, completedTaskGuids, tierTasks);
  });
}

/**
 * Get progress information to the next tier (completion-based)
 */
export function getProgressToNextTier(
  completedTaskGuids: string[],
  allTasks: TaskStatus[],
  campaign: ChallengeCampaign,
  currentTier: TierName
): {
  currentTier: TierName;
  nextTier: TierName | null;
  tasksCompletedInCurrentTier: number;
  totalTasksInCurrentTier: number;
  progressPercentage: number;
};

/**
 * Get progress information to the next tier (backward compatibility)
 * @deprecated Use completion-based overload instead
 */
export function getProgressToNextTier(totalPoints: number): {
  currentTier: TierName;
  nextTier: TierName | null;
  pointsToNextTier: number;
  progressPercentage: number;
  currentPoints: number;
};

export function getProgressToNextTier(
  completedTaskGuidsOrPoints: string[] | number,
  allTasksOrUndefined?: TaskStatus[],
  campaignOrUndefined?: ChallengeCampaign,
  currentTierOrUndefined?: TierName
):
  | {
      currentTier: TierName;
      nextTier: TierName | null;
      tasksCompletedInCurrentTier: number;
      totalTasksInCurrentTier: number;
      progressPercentage: number;
    }
  | {
      currentTier: TierName;
      nextTier: TierName | null;
      pointsToNextTier: number;
      progressPercentage: number;
      currentPoints: number;
    } {
  // Backward compatibility: point-based progress
  if (typeof completedTaskGuidsOrPoints === "number") {
    const totalPoints = completedTaskGuidsOrPoints;
    const currentTier = getUserTier(totalPoints);
    const currentTierIndex = TIER_ORDER.indexOf(currentTier);
    const nextTier =
      currentTierIndex < TIER_ORDER.length - 1
        ? TIER_ORDER[currentTierIndex + 1]
        : null;

    if (!nextTier) {
      // User is at max tier (Ambassador)
      return {
        currentTier,
        nextTier: null,
        pointsToNextTier: 0,
        progressPercentage: 100,
        currentPoints: totalPoints,
      };
    }

    const currentTierConfig = TIER_CONFIG[currentTier];
    const nextTierConfig = TIER_CONFIG[nextTier];
    const pointsInCurrentTier = totalPoints - currentTierConfig.minPoints;
    const pointsNeededForNextTier =
      nextTierConfig.minPoints - currentTierConfig.minPoints;
    const pointsToNextTier = nextTierConfig.minPoints - totalPoints;
    const progressPercentage = Math.min(
      100,
      Math.floor((pointsInCurrentTier / pointsNeededForNextTier) * 100)
    );

    return {
      currentTier,
      nextTier,
      pointsToNextTier,
      progressPercentage,
      currentPoints: totalPoints,
    };
  }

  // New completion-based progress
  const completedTaskGuids = completedTaskGuidsOrPoints;
  const allTasks = allTasksOrUndefined!;
  const campaign = campaignOrUndefined!;
  const currentTier = currentTierOrUndefined!;

  const currentTierIndex = TIER_ORDER.indexOf(currentTier);
  const nextTier =
    currentTierIndex < TIER_ORDER.length - 1
      ? TIER_ORDER[currentTierIndex + 1]
      : null;

  const currentTierTasks = getTasksInTier(currentTier, allTasks, campaign);
  const tasksCompletedInCurrentTier = currentTierTasks.filter((task) =>
    completedTaskGuids.includes(task.taskGuid)
  ).length;
  const totalTasksInCurrentTier = currentTierTasks.length;

  const progressPercentage =
    totalTasksInCurrentTier > 0
      ? (tasksCompletedInCurrentTier / totalTasksInCurrentTier) * 100
      : 0;

  return {
    currentTier,
    nextTier,
    tasksCompletedInCurrentTier,
    totalTasksInCurrentTier,
    progressPercentage,
  };
}

/**
 * Get tier configuration by name
 */
export const getTierConfig = (tierName: TierName): TierConfig => {
  return TIER_CONFIG[tierName];
};

/**
 * Get the unlock message for a tier (completion-based)
 */
export const getTierUnlockMessage = (tierName: TierName): string => {
  switch (tierName) {
    case "TIER1":
      return "Start your journey";
    case "TIER2":
      return "Complete all Tier 1 tasks to unlock";
    case "TIER3":
      return "Complete all Tier 2 tasks to unlock";
    case "AMBASSADOR":
      return "Complete all Tier 3 tasks to unlock";
  }
};

/**
 * Get all tiers that have tasks (now includes Ambassador)
 */
export const getTaskTiers = (): TierName[] => {
  return TIER_ORDER;
};

/**
 * Get all tasks that belong to a specific tier (only enabled tasks)
 */
export const getTasksInTier = (
  tierName: TierName,
  allTasks: TaskStatus[],
  campaign: ChallengeCampaign
): TaskStatus[] => {
  return allTasks.filter((task) => {
    // Skip disabled tasks
    if (!task.isEnabled) return false;

    const challengeTask = campaign.tasks?.find((t) => t.id === task.taskGuid);
    const maxPoints = challengeTask
      ? parseFloat(challengeTask.points)
      : parseFloat(task.maxPoints);
    return getTaskTier(maxPoints, task.taskGuid) === tierName;
  });
};

/**
 * Check if all tasks in a tier are completed
 */
export const isTierCompleted = (
  _tierName: TierName,
  completedTaskGuids: string[],
  tierTasks: TaskStatus[]
): boolean => {
  if (tierTasks.length === 0) return true; // Empty tier = completed
  return tierTasks.every((task) => completedTaskGuids.includes(task.taskGuid));
};

/**
 * Get all previous tiers that must be completed before unlocking target tier
 */
export const getPreviousTiers = (tierName: TierName): TierName[] => {
  const tierIndex = TIER_ORDER.indexOf(tierName);
  return TIER_ORDER.slice(0, tierIndex);
};
